import React from "react";
import { DotLegalEmptyState, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../../localization/useTranslation";
import { Box, TableCell } from "@mui/material";
import { LegalEntityAuditApiModel, LegalEntityAuditRowModel, LegalEntityAuditStatus } from "../../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { useUrlProvider } from "../../../../useUrlProvider";
import DotLegalLink from "../../dotLegalLink/DotLegalLink";
import NoSharings from "../../../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import { usePlanContext } from "../../../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../../../auth/userContextProvider/UserContextProvider";
import QuestionnaireReportButtons from "../questionnaireReportButtons/QuestionnaireReportButtons";
import QuestionnaireStatus from "../questionnaireStatus/QuestionnaireStatus";
import QuestionnaireRisk from "../questionnaireRisk/QuestionnaireRisk";
import QuestionnaireReportOverflowButtons from "../questionnaireReportButtons/QuestionnaireReportOverflowButtons";
import { BaseViewModel } from "../../../../system/systemOverview/SystemOverview.types";
import AddToAuditGroup from "../../../../legalEntity/legalEntityAuditTab/addToAuditGroup/AddToAuditGroup";
import { isNullOrWhitespace } from "../../../stringOperations";
import LegalEntityAuditTableOverflowMenu from "../../../../legalEntity/legalEntityAuditTab/legalEntityAuditTableOverflowMenu/LegalEntityAuditTableOverflowMenu";
import QuestionnaireTemplateLink from "../../../../legalEntity/legalEntityAuditTab/QuestionnaireTemplateLink";
import SystemLinks from "../../../../system/SystemLinks";
import QuestionnaireEvaluation from "../questionnaireEvaluation/QuestionnaireEvaluation";
import PurpleLockSvg from "../../../svgs/purpleLock.svg?react";
import TrialUsageWarning from "../../TrialUsageWarning";
import TrialExpiredRowWrapper from "../../TrialExpiredRowWrapper";
import { EntityType } from "../../../Common.types";
import DotLegalNoteDialog from "@common/components/dotlegalNote/dotLegalNoteDialog/DotLegalNoteDialog";
import TruncatedMentionText from "@common/components/mentionInput/TruncatedMentionText";
import { noteExcludedFromSearch } from "@common/tableSearch";
import ProcessingActivityLinks from "../../../../processingActivity/processingActivityLinks/ProcessingActivityLinks";
import { PlanLimit } from "../../../../auth/planProvider/PlanProvider.types";
import useExternalAuditTableHooks from "./ExternalAuditTable.hooks";
import { AuditType } from "../auditTab/AuditTab.types";
import { auditTabLocalStorageKey } from "../auditTab/AuditTab.hooks";
import { useDocumentPreviewManager } from "@common/components/documentPreview/useDocumentPreviewManager.ts";
import { isPreviewSupported } from "@common/components/documentPreview/DocumentPreview.hooks.ts";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";

export interface IExternalAuditTableProps {
    data: Array<LegalEntityAuditApiModel> | undefined;
    isLoading: boolean;
    refetch: () => void;
    pageType: "legalEntity" | "processingActivity" | "system";
    emptyStateText: string;
    planLimit?: PlanLimit;
    legalEntityId?: string;
    legalEntityName?: string;
    processingActivityId?: string;
    processingActivityName?: string;
    sessionStorageKey?: string;
}

function ExternalAuditTable(props: IExternalAuditTableProps) {
    const { translateString, translateDate } = useTranslation();
    const { vendorManagementFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const urlProvider = useUrlProvider();

    const {
        audits,
        hasAudits,
        showAddToAuditGroup,
        setShowAddToAuditGroup,
        onAuditAddedToAuditGroup,
        onAuditClosed,
        onAuditReopened,
        onAuditDeleted,
        onDownloadReportAsPdf,
        isDownloadingPdf,
        getReportPdfUrl,
        onResponsibleChanged,
        onContactChanged,
        onReminderSent,
        onAuditEvaluated,
        onRemindersToggled,
        downloadingDocument,
        onDownloadClick,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        getNoteMenuItem,
        noteDialogProps,
    } = useExternalAuditTableHooks(props);

    const documents =
        audits
            ?.filter((x) => x.document)
            .map((x) => ({
                ...x.document!,
                entityId: x.legalEntity?.id ?? props.legalEntityId,
                entityTitle: x.legalEntity?.name ?? props.legalEntityName,
            })) ?? [];
    const { previewableDocuments, handleDocumentWithTypeAndIdClick } = useDocumentPreviewManager({
        documents: documents,
        entityType: EntityType.LegalEntity,
        entityId: props.legalEntityId,
    });

    const legalEntityPermissions = permissions.companyPermissions;

    function getHeaders() {
        const headers = Array<ITableHeader<LegalEntityAuditRowModel>>();
        headers.push({ property: "questionnaireName", text: translateString("template"), align: "left", showOnMobile: true });
        headers.push({ property: "questionnaireVersion", text: translateString("version"), align: "left", showOnMobile: true });
        headers.push({ property: "uniqueIdentifier", text: "Id", align: "left", showOnMobile: true });
        headers.push({ property: "sent", text: translateString("legalEntityAuditSent"), align: "left", showOnMobile: true });
        headers.push({ property: "deadline", text: translateString("deadline"), align: "left", showOnMobile: true });
        if (props.pageType === "legalEntity") {
            headers.push({ property: "relatedEntitiesString", text: translateString("assetsOrProcessingActivities"), align: "left", showOnMobile: true });
        }
        if (props.pageType === "system" || props.pageType === "processingActivity") {
            headers.push({ property: "legalEntityName", text: translateString("legalEntity"), align: "left", showOnMobile: true });
        }
        headers.push({ property: "contactName", text: translateString("contact"), align: "left", showOnMobile: true });
        headers.push({ property: "additionalRecipientNames", text: translateString("additionalRecipients"), align: "left", showOnMobile: true });
        headers.push({ property: "responsibleName", text: translateString("responsible"), align: "left", showOnMobile: true });
        headers.push({ property: "sender", text: translateString("sentFrom"), align: "left", showOnMobile: true });
        headers.push({ property: "auditGroupName", text: translateString("auditGroup"), align: "left", showOnMobile: true });
        headers.push({ property: "statusName", text: translateString("status"), align: "left", showOnMobile: true });
        headers.push({ property: "riskLevelName", text: translateString("riskLevel"), align: "left", showOnMobile: true });
        headers.push({ property: "scoreName", text: translateString("score"), align: "left", showOnMobile: true });
        headers.push({ property: "evaluation", text: translateString("evaluation"), align: "left", showOnMobile: true });
        headers.push({ property: "evaluationName", text: "", align: "left", showOnMobile: true, hideHeader: true });
        headers.push({ property: "report", text: translateString("legalEntityAuditReport"), align: "left", showOnMobile: true });
        headers.push({ property: "reminderSent", text: translateString("headerReminderSent"), align: "left", showOnMobile: true });
        headers.push({ property: "notePlainText", text: translateString("note"), align: "left", showOnMobile: true });
        return headers;
    }

    function getMenuItems(row: LegalEntityAuditRowModel) {
        let menuItems = [];

        if (row.partOfCurrentPlan) {
            // An audit has no page of its own, so the menu is the only way to its note.
            if (legalEntityPermissions.edit) {
                menuItems.push(getNoteMenuItem(row));
            }

            if (vendorManagementFeatures.hasVendorManagement && !row.auditGroup && legalEntityPermissions.edit) {
                menuItems.push({
                    menuItem: translateString("addToAuditGroup"),
                    onClick: () => {
                        setShowAddToAuditGroup(row.id);
                    },
                });
            }

            if (vendorManagementFeatures.hasVendorManagement && legalEntityPermissions.create) {
                if (props.planLimit?.limitReached) {
                    menuItems.push({
                        menuItem: (
                            <Box sx={{ cursor: "default", display: "flex", alignContent: "center" }}>
                                <Box component={"span"}>{translateString("sendCopyOfAudit")}</Box>
                                <Box component={"span"} sx={{ marginLeft: 0.5, height: 0 }}>
                                    <PurpleLockSvg />
                                </Box>
                            </Box>
                        ),
                        tooltip: translateString("auditLimitReached"),
                        disabled: true,
                    });
                }
            }
        }

        return menuItems;
    }

    function getAuditGroupUrl(auditGroup?: BaseViewModel) {
        if (!auditGroup) {
            return undefined;
        }

        if (!vendorManagementFeatures.auditGroups) {
            return auditGroup.name;
        }

        return (
            <Box key={auditGroup.id} component={"span"} sx={{ fontWeight: "bold" }}>
                <DotLegalLink linkColor={"primary"} to={urlProvider.getAuditGroupUrl(auditGroup.id)}>
                    {auditGroup.name}
                </DotLegalLink>
            </Box>
        );
    }

    function getStatus(row: LegalEntityAuditApiModel) {
        return (
            <QuestionnaireStatus
                showQuestionsStatistics={row.showQuestionsStatistics}
                numberOfQuestionsAnswered={row.numberOfQuestionsAnswered}
                totalNumberOfQuestions={row.totalNumberOfQuestions}
                status={row.status}
                lastestRecipientUpdate={row.lastestRecipientUpdate}
                onClick={
                    row.status === LegalEntityAuditStatus.AuditOpinion && row.document
                        ? () => {
                              if (isNullOrWhitespace(row.document!.link) && isPreviewSupported(row.document!.fileName!)) {
                                  handleDocumentWithTypeAndIdClick(row.document!, EntityType.LegalEntity, props.legalEntityId ?? row.legalEntity!.id);
                              } else {
                                  onDownloadClick(
                                      row.document!.documentId,
                                      row.document!.dataId!,
                                      row.document!.fileName ?? "",
                                      row.document!.link,
                                      row.legalEntity?.id ?? props.legalEntityId ?? ""
                                  );
                              }
                          }
                        : undefined
                }
            />
        );
    }

    function getRisk(row: LegalEntityAuditApiModel) {
        return <QuestionnaireRisk riskLevel={row.riskLevel} riskName={row.riskName} scoreEnabled={row.scoreEnabled} status={row.status} />;
    }

    function getDownloadAuditReportModel(row: LegalEntityAuditRowModel) {
        return {
            legalEntityAuditId: row.id,
            ...row,
            legalEntityName: row.legalEntityName ?? props.legalEntityName ?? "",
            questionnaireResponseId: row.questionnaireResponseId!,
            responseExternalId: row.responseExternalId!,
        };
    }

    function getReportButtons(row: LegalEntityAuditRowModel) {
        if (!row.questionnaireResponseId) {
            return undefined;
        }

        let showButtons = row.status !== LegalEntityAuditStatus.Opened && row.status !== LegalEntityAuditStatus.Sent && !row.isClosed;
        let isDownloading = isDownloadingPdf(row.id);
        let reportUrl = getReportPdfUrl(
            row.questionnaireResponseId,
            row.deadline,
            row.sender,
            row.legalEntityName ?? props.legalEntityName ?? "",
            false,
            row.hasRecommendations
        );

        if (!row.partOfCurrentPlan) {
            return undefined;
        }

        if (row.scoreEnabled || row.hasRecommendations) {
            return (
                <QuestionnaireReportOverflowButtons
                    reportPdfUrl={reportUrl}
                    reportPdfMenuItems={getReportButtonMenuItems(row)}
                    showButtons={showButtons}
                    isDownloadingPdf={isDownloading}
                />
            );
        }

        return (
            <QuestionnaireReportButtons
                isDownloadingPdf={isDownloading}
                onDownloadReportPdf={() => onDownloadReportAsPdf(getDownloadAuditReportModel(row), true, false)}
                reportPdfUrl={reportUrl}
                showButtons={showButtons}
            />
        );
    }

    function getReportButtonMenuItems(row: LegalEntityAuditRowModel) {
        if (row.scoreEnabled) {
            return [
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithScoreAndRecommendations") : translateString("downloadWithScore"),
                    onClick: () => onDownloadReportAsPdf(getDownloadAuditReportModel(row), false, row.hasRecommendations),
                },
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithoutScoreAndRecommendations") : translateString("downloadWithoutScore"),
                    onClick: () => onDownloadReportAsPdf(getDownloadAuditReportModel(row), true, false),
                },
            ];
        } else {
            return [
                {
                    menuItem: translateString("downloadWithRecommendations"),
                    onClick: () => onDownloadReportAsPdf(getDownloadAuditReportModel(row), false, true),
                },
                {
                    menuItem: translateString("downloadWithoutRecommendations"),
                    onClick: () => onDownloadReportAsPdf(getDownloadAuditReportModel(row), false, false),
                },
            ];
        }
    }

    return (
        <>
            {hasAudits ? (
                <>
                    {props.planLimit?.limitExceeded && <TrialUsageWarning textKey={translateString("externalAuditsLimitExceededWarning")} marginBottom={2} />}
                    <DotLegalTableWithFilterBar
                        headers={getHeaders()}
                        columnsExcludedFromFilter={noteExcludedFromSearch}
                        rowsPerPage={30}
                        defaultOrderBy={"uniqueIdentifier"}
                        defaultOrder={"desc"}
                        isLoading={props.isLoading}
                        clickableRows={false}
                        hasOverflowMenu
                        data={audits}
                        filterBar={{
                            isLoading: props.isLoading,
                            filterData: getFilterOptions(),
                            fetchFilterData: true,
                            onMenuClose: onFilterChange,
                            onRemove: onFilterChange,
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                        }}
                        emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noData")}
                        renderRow={(row, i) => (
                            <TrialExpiredRowWrapper
                                key={i}
                                tooltip={""}
                                onNonExpiredClick={() => {}}
                                expired={!row.partOfCurrentPlan}
                                planUpgradeText={translateString("auditLimitReached")}
                            >
                                <TableCell>
                                    <QuestionnaireTemplateLink type={"audit"} id={row.questionnaireId} name={row.questionnaireName} />
                                </TableCell>
                                <TableCell>{row.questionnaireVersion}</TableCell>
                                <TableCell>{row.uniqueIdentifier}</TableCell>
                                <TableCell>{row.sent ? translateDate(row.sent) : ""}</TableCell>
                                <TableCell>{translateDate(row.deadline)}</TableCell>
                                {props.pageType === "legalEntity" && (
                                    <TableCell>
                                        {row.assets.length > 0 && (
                                            <SystemLinks
                                                tab={vendorManagementFeatures.systemAssociateInternalAssessments ? "2" : undefined}
                                                systems={row.assets}
                                                onClick={() => localStorage.setItem(auditTabLocalStorageKey, AuditType.External.toString())}
                                            />
                                        )}
                                        {row.processingActivities.length > 0 && (
                                            <ProcessingActivityLinks
                                                tab={vendorManagementFeatures.processingActivityAssociateInternalAssessments ? "2" : undefined}
                                                processingActivityLinks={row.processingActivities}
                                                onClick={() => localStorage.setItem(auditTabLocalStorageKey, AuditType.External.toString())}
                                            />
                                        )}
                                    </TableCell>
                                )}
                                {(props.pageType === "system" || props.pageType === "processingActivity") && (
                                    <TableCell>
                                        {row.legalEntity && (
                                            <DotLegalLink bold linkColor={"primary"} to={urlProvider.getLegalEntityUrl(row.legalEntity.id)}>
                                                {row.legalEntity.name}
                                            </DotLegalLink>
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>{row.contactName}</TableCell>
                                <TableCell>{row.additionalRecipientNames}</TableCell>
                                <TableCell>{row.responsibleName}</TableCell>
                                <TableCell>{row.sender}</TableCell>
                                <TableCell>{getAuditGroupUrl(row.auditGroup)}</TableCell>
                                <TableCell>{getStatus(row)}</TableCell>
                                <TableCell>{getRisk(row)}</TableCell>
                                <TableCell align="right">{row.scoreName}</TableCell>
                                <TableCell>
                                    <QuestionnaireEvaluation evaluation={row.evaluation} evaluationStatus={row.evaluationStatus} />
                                </TableCell>
                                <TableCell>{getReportButtons(row)}</TableCell>
                                <TableCell>
                                    {row.remindersPaused ? translateString("remindersPaused") : row.reminderSent ? translateDate(row.reminderSent) : ""}
                                </TableCell>
                                <TableCell>{row.partOfCurrentPlan && <TruncatedMentionText value={row.note} />}</TableCell>
                                <TableCell
                                    align={"right"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    {!props.isLoading && (
                                        <LegalEntityAuditTableOverflowMenu
                                            partOfCurrentPlan={row.partOfCurrentPlan}
                                            key={row.id}
                                            auditId={row.id}
                                            isClosed={row.isClosed}
                                            status={row.status}
                                            canBeDeleted={row.canBeDeleted}
                                            questionnaireName={row.questionnaireName}
                                            deadline={row.deadline}
                                            auditNote={row.note}
                                            responsibleId={row.responsible.id}
                                            contactId={row.contact?.id ?? null}
                                            contactName={row.contact?.name ?? null}
                                            additionalRecipientIds={row.additionalRecipients.map((additionalRecipient) => additionalRecipient.id)}
                                            legalEntityId={props.legalEntityId!}
                                            responseExternalId={row.responseExternalId}
                                            createdAsAuditorReport={row.createdAsAuditorReport}
                                            evaluation={row.evaluation}
                                            evaluationStatus={row.evaluationStatus}
                                            onAuditClosed={onAuditClosed}
                                            onAuditReopened={onAuditReopened}
                                            onAuditDeleted={onAuditDeleted}
                                            onResponsibleChanged={onResponsibleChanged}
                                            onContactChanged={onContactChanged}
                                            onReminderSent={onReminderSent}
                                            additionalMenuItems={getMenuItems(row)}
                                            disableChangeResponsible={row.auditGroup !== null && row.auditGroup !== undefined}
                                            onAuditEvaluated={onAuditEvaluated}
                                            onRemindersToggled={onRemindersToggled}
                                            remindersPaused={row.remindersPaused}
                                            questionnaireResponseId={row.questionnaireResponseId}
                                            questionnaireId={row.questionnaireId}
                                            legalEntityName={props.legalEntityName!}
                                        />
                                    )}
                                </TableCell>
                            </TrialExpiredRowWrapper>
                        )}
                    />
                </>
            ) : (
                <DotLegalEmptyState icon={<NoSharings />} text={props.emptyStateText} />
            )}

            {audits && !isNullOrWhitespace(showAddToAuditGroup) && (
                <AddToAuditGroup
                    auditId={showAddToAuditGroup!}
                    onCloseDialog={() => setShowAddToAuditGroup(undefined)}
                    selectableAuditGroups={audits.find((x) => x.id === showAddToAuditGroup)!.selectableAuditGroups}
                    onAddedToAuditGroup={onAuditAddedToAuditGroup}
                />
            )}

            {noteDialogProps && <DotLegalNoteDialog {...noteDialogProps} />}
        </>
    );
}

export default ExternalAuditTable;
